import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import CoverImageUpload from '../challenge-creation/components/CoverImageUpload';
import { Checkbox } from '../../components/ui/Checkbox';
import { useAuth } from '../../auth/AuthProvider';
import { compressImageUnderBytes } from '../../utils/imageCompression';
import { getCachedSignedUrl, makeSafeObjectPath, uploadToBucket } from '../../utils/storage';
import { supabase } from '../../lib/supabaseClient';
import AICoverImageWizard from './components/AICoverImageWizard';
import AIChallengeRecommendWizard from './components/AIChallengeRecommendWizard';

const ProjectCreation = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const { user } = useAuth();
  const [isAICoverOpen, setIsAICoverOpen] = useState(false);
  const [isAIChallengeOpen, setIsAIChallengeOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const parseTags = (raw) => {
    const items = String(raw || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    return Array.from(new Set(items));
  };

  const [notice, setNotice] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    primaryLabel: '확인',
    onPrimary: null,
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    coverImage: null,
    coverImageFile: null,
    tagsText: '',
    isPublic: true,
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = '목표 제목을 입력하세요';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = '목표 설명을 입력하세요';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const openNotice = ({ title, message, variant = 'info', primaryLabel = '확인', onPrimary = null }) => {
    setNotice({ open: true, title, message, variant, primaryLabel, onPrimary });
  };

  const closeNotice = () => {
    setNotice((prev) => ({ ...prev, open: false }));
  };

  const normalizeGoalType = (raw) => (raw === 'thirty-day' ? 'thirty_day' : raw);

  const computeTotalGoals = (goalType, parameters) => {
    if (goalType === 'fixed') return Number(parameters?.targetCount || 0);
    if (goalType === 'split') return Number(parameters?.totalGoal || 0);
    if (goalType === 'thirty_day') return 30;
    return 0;
  };

  const handleCreateProject = async () => {
    if (!validate()) {
      openNotice({
        title: '입력 확인',
        message: '필수 항목을 모두 입력해주세요',
        variant: 'warning',
      });
      return;
    }

    if (!user?.id) {
      openNotice({
        title: '로그인이 필요합니다',
        message: '계속하려면 로그인해주세요.',
        variant: 'warning',
        primaryLabel: '로그인',
        onPrimary: () => navigate('/login-registration'),
      });
      return;
    }

    setIsSaving(true);

    try {
      const { data: created, error: createError } = await supabase
        .from('ch_projects')
        .insert({
          owner_id: user.id,
          title: formData?.title?.trim(),
          description: formData?.description?.trim(),
          tags: parseTags(formData?.tagsText),
          is_public: !!formData?.isPublic,
          status: 'active',
          is_archived: false,
          total_challenges: 0,
          completed_challenges: 0,
          completed_proofs: 0,
          average_stars: 0,
        })
        .select('*')
        .single();

      if (createError) {
        openNotice({
          title: '목표 생성 실패',
          message: createError?.message || '목표 생성에 실패했습니다',
          variant: 'error',
        });
        return;
      }

      let coverImagePath = '';

      const file = formData?.coverImageFile;
      if (file) {
        if (!file?.type?.startsWith('image/')) {
          openNotice({
            title: '업로드 실패',
            message: '이미지 파일만 업로드할 수 있습니다',
            variant: 'error',
          });
          return;
        }

        let blob = file;
        if (file.size > 1024 * 1024) {
          blob = await compressImageUnderBytes(file, 1024 * 1024);
        }

        const path = makeSafeObjectPath({
          userId: user.id,
          originalName: file?.name,
          prefix: 'covers',
        });

        const { error: uploadError } = await uploadToBucket({
          bucket: 'project-covers',
          path,
          blob,
          contentType: blob?.type || 'image/jpeg',
          cacheControl: '31536000',
        });

        if (uploadError) {
          openNotice({
            title: '업로드 실패',
            message: uploadError?.message || '이미지 업로드에 실패했습니다',
            variant: 'error',
          });
          return;
        }

        coverImagePath = path;
      }

      if (coverImagePath) {
        const { error: updateError } = await supabase
          .from('ch_projects')
          .update({
            cover_image_path: coverImagePath,
            cover_image_alt: formData?.title?.trim() || 'Project cover image',
          })
          .eq('id', created.id);

        if (updateError) {
          openNotice({
            title: '저장 실패',
            message: updateError?.message || '목표 이미지 저장에 실패했습니다',
            variant: 'error',
          });
          return;
        }
      }

      if (coverImagePath) {
        await getCachedSignedUrl({ bucket: 'project-covers', path: coverImagePath });
      }

      if (selectedChallenge) {
        const normalizedGoalType = normalizeGoalType(selectedChallenge?.goalType) || 'fixed';
        const parameters = selectedChallenge?.parameters && typeof selectedChallenge.parameters === 'object'
          ? selectedChallenge.parameters
          : {};
        const settings = selectedChallenge?.settings && typeof selectedChallenge.settings === 'object'
          ? selectedChallenge.settings
          : {};

        const totalGoals = computeTotalGoals(normalizedGoalType, parameters);
        const targetCount = normalizedGoalType === 'fixed' ? Number(parameters?.targetCount || 0) : null;

        const { data: chCreated, error: chCreateError } = await supabase
          .from('ch_challenges')
          .insert({
            project_id: created.id,
            owner_id: user.id,
            title: String(selectedChallenge?.title || '').trim(),
            description: String(selectedChallenge?.description || '').trim(),
            tags: Array.isArray(selectedChallenge?.tags) ? selectedChallenge.tags : [],
            goal_type: normalizedGoalType,
            target_count: targetCount,
            total_goals: totalGoals,
            default_public: !!settings?.defaultPublic,
            sequential_unlock: !!settings?.sequentialUnlock,
            meta: {
              parameters,
              settings,
            },
          })
          .select('*')
          .single();

        if (chCreateError) {
          openNotice({
            title: '챌린지 생성 실패',
            message: chCreateError?.message || '챌린지 생성에 실패했습니다',
            variant: 'error',
          });
          return;
        }

        let challengeCoverPath = '';
        const chFile = selectedChallenge?.coverImageFile;

        if (chFile) {
          if (!chFile?.type?.startsWith('image/')) {
            openNotice({
              title: '업로드 실패',
              message: '이미지 파일만 업로드할 수 있습니다',
              variant: 'error',
            });
            return;
          }

          let blob = chFile;
          if (chFile.size > 1024 * 1024) {
            blob = await compressImageUnderBytes(chFile, 1024 * 1024);
          }

          const path = makeSafeObjectPath({
            userId: user.id,
            originalName: chFile?.name,
            prefix: 'covers',
          });

          const { error: uploadError } = await uploadToBucket({
            bucket: 'challenge-covers',
            path,
            blob,
            contentType: blob?.type || 'image/jpeg',
            cacheControl: '31536000',
          });

          if (uploadError) {
            openNotice({
              title: '업로드 실패',
              message: uploadError?.message || '이미지 업로드에 실패했습니다',
              variant: 'error',
            });
            return;
          }

          challengeCoverPath = path;
        }

        if (challengeCoverPath) {
          const { error: updateError } = await supabase
            .from('ch_challenges')
            .update({
              cover_image_path: challengeCoverPath,
              cover_image_alt: String(selectedChallenge?.title || '').trim() || 'Challenge cover image',
            })
            .eq('id', chCreated.id);

          if (updateError) {
            openNotice({
              title: '저장 실패',
              message: updateError?.message || '챌린지 이미지 저장에 실패했습니다',
              variant: 'error',
            });
            return;
          }

          await getCachedSignedUrl({ bucket: 'challenge-covers', path: challengeCoverPath });
        }

        await supabase
          .from('ch_projects')
          .update({ total_challenges: 1 })
          .eq('id', created.id);
      }

      openNotice({
        title: '생성 완료',
        message: '목표가 생성되었습니다!',
        variant: 'success',
        primaryLabel: '목표로 이동',
        onPrimary: () => navigate(`/project-detail?id=${created.id}`),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const contextActions = [
    {
      label: '목표 생성',
      icon: 'Check',
      variant: 'default',
      onClick: handleCreateProject,
      description: '목표를 생성합니다'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 md:py-8 lg:py-12 mt-16 mb-20 lg:mb-8">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-primary/20 rounded-md flex items-center justify-center">
              <Icon name="Plus" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                새 목표 만들기
              </h1>
              <p className="text-sm md:text-base text-muted-foreground caption mt-1">
                제목과 설명, 대표 이미지를 입력하세요
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8">
              <div>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                  기본 정보
                </h2>
                <p className="text-sm text-muted-foreground caption">
                  목표의 기본 정보를 입력하세요
                </p>
              </div>

              <div className="mt-4">
                <Input
                  type="text"
                  label="목표 제목"
                  description="목표를 대표하는 제목을 입력하세요"
                  placeholder="예: 2026년 건강한 삶 목표"
                  value={formData?.title}
                  onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                  error={errors?.title}
                  required
                  maxLength={50}
                />
              </div>

              <div className="mt-4">
                <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                  목표 설명 <span className="text-destructive">*</span>
                </label>
                <p className="text-xs md:text-sm text-muted-foreground caption mb-3">
                  목표의 목표와 방향을 설명하세요
                </p>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="예: 규칙적인 운동과 건강한 식습관을 통해 더 나은 삶의 질을 추구합니다."
                  rows={6}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                />
                {errors?.description && (
                  <div className="flex items-start space-x-2 mt-2">
                    <Icon
                      name="AlertCircle"
                      size={14}
                      color="var(--color-destructive)"
                      className="flex-shrink-0 mt-0.5"
                    />
                    <p className="text-xs text-destructive">{errors?.description}</p>
                  </div>
                )}
              </div>

              <div className="mt-4">
                <Input
                  type="text"
                  label="태그"
                  description="쉼표(,)로 구분하여 입력하세요"
                  placeholder="예: 건강, 운동, 습관"
                  value={formData?.tagsText}
                  onChange={(e) => setFormData({ ...formData, tagsText: e?.target?.value })}
                  maxLength={200}
                />
              </div>

              <div className="mt-4">
                <Checkbox
                  label="공유(랜딩 공개)"
                  description="체크하면 랜딩 페이지에 이 목표가 노출됩니다"
                  checked={!!formData?.isPublic}
                  onChange={(e) => setFormData((prev) => ({ ...prev, isPublic: e?.target?.checked }))}
                />
              </div>

              <div className="mt-4">
                <CoverImageUpload
                  coverImage={formData?.coverImage}
                  headerActions={(
                    <Button
                      variant="outline"
                      iconName="Sparkles"
                      iconPosition="left"
                      onClick={() => setIsAICoverOpen(true)}
                      disabled={isSaving}
                    >
                      AI 커버 이미지 생성
                    </Button>
                  )}
                  onImageChange={(image, file) => {
                    if (!image || !file) {
                      setFormData({ ...formData, coverImage: null, coverImageFile: null });
                      return;
                    }

                    if (!file?.type?.startsWith('image/')) {
                      setErrors((prev) => ({ ...prev, coverImage: '이미지 파일만 업로드할 수 있습니다' }));
                      setFormData({ ...formData, coverImage: null, coverImageFile: null });
                      return;
                    }

                    setErrors((prev) => ({ ...prev, coverImage: '' }));
                    setFormData({ ...formData, coverImage: image, coverImageFile: file });
                  }}
                  error={errors?.coverImage}
                />
              </div>

              <div className="mt-4 rounded-lg border border-border bg-background p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm md:text-base font-heading font-semibold text-foreground">AI 챌린지 추천</div>
                    <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
                      목표 제목/설명을 바탕으로 챌린지를 추천하고, 선택하면 커버 이미지까지 미리 생성합니다
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    iconName="Sparkles"
                    iconPosition="left"
                    onClick={() => setIsAIChallengeOpen(true)}
                    disabled={isSaving}
                  >
                    AI 챌린지 추천
                  </Button>
                </div>

                {selectedChallenge ? (
                  <div className="mt-4 rounded-lg border border-border bg-muted/30 p-3">
                    <div className="text-sm font-heading font-semibold text-foreground">
                      선택됨: {selectedChallenge?.title}
                    </div>
                    {selectedChallenge?.coverImageDataUrl ? (
                      <div className="mt-3">
                        <div className="aspect-video rounded-md overflow-hidden border border-border bg-background">
                          <Image
                            src={selectedChallenge.coverImageDataUrl}
                            alt="Selected challenge cover"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    ) : null}
                    <div className="mt-3 flex items-center justify-end">
                      <Button
                        variant="outline"
                        onClick={() => setSelectedChallenge(null)}
                        disabled={isSaving}
                      >
                        선택 해제
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/project-dashboard')}
                  disabled={isSaving}
                >
                  취소
                </Button>
                <Button
                  variant="default"
                  iconName="Check"
                  iconPosition="left"
                  onClick={handleCreateProject}
                  loading={isSaving}
                >
                  목표 생성
                </Button>
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-24">
              <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                  미리보기
                </h2>
                <p className="text-sm text-muted-foreground caption mb-4">
                  입력한 정보가 목표 카드에 어떻게 표시될지 확인하세요
                </p>

                <div className="rounded-lg overflow-hidden border border-border bg-background">
                  <div className="aspect-video bg-muted">
                    {formData?.coverImage ? (
                      <Image
                        src={formData.coverImage}
                        alt={formData?.title?.trim() ? `${formData.title} cover preview` : 'Project cover preview'}
                        className="w-full h-full object-cover"
                      />
                    ) : null}
                  </div>
                  <div className="p-4">
                    <div className="text-base font-heading font-semibold text-foreground line-clamp-2 mb-2">
                      {formData?.title || '목표 제목'}
                    </div>
                    <div className="text-sm text-muted-foreground caption line-clamp-3">
                      {formData?.description || '목표 설명'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <AICoverImageWizard
        open={isAICoverOpen}
        onClose={() => setIsAICoverOpen(false)}
        title={formData?.title}
        description={formData?.description}
        onApply={(image, file) => {
          setErrors((prev) => ({ ...prev, coverImage: '' }));
          setFormData((prev) => ({ ...prev, coverImage: image, coverImageFile: file }));
        }}
      />

      <AIChallengeRecommendWizard
        open={isAIChallengeOpen}
        onClose={() => setIsAIChallengeOpen(false)}
        projectTitle={formData?.title}
        projectDescription={formData?.description}
        onSelect={(item) => {
          setSelectedChallenge(item);
        }}
      />

      {notice?.open && (
        <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-gaming-lg p-6 md:p-8 max-w-md w-full border border-border">
            <div className="flex items-start space-x-4 mb-6">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-md flex items-center justify-center ${
                  notice?.variant === 'success'
                    ? 'bg-success/20'
                    : notice?.variant === 'error'
                      ? 'bg-destructive/20'
                      : notice?.variant === 'warning'
                        ? 'bg-warning/20'
                        : 'bg-primary/20'
                }`}
              >
                <Icon
                  name={notice?.variant === 'success' ? 'CheckCircle' : notice?.variant === 'error' ? 'XCircle' : notice?.variant === 'warning' ? 'AlertTriangle' : 'Info'}
                  size={24}
                  color={notice?.variant === 'success' ? 'var(--color-success)' : notice?.variant === 'error' ? 'var(--color-destructive)' : notice?.variant === 'warning' ? 'var(--color-warning)' : 'var(--color-primary)'}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">{notice?.title || '안내'}</h3>
                <p className="text-sm text-muted-foreground">{notice?.message}</p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={closeNotice}
                disabled={isSaving}
              >
                닫기
              </Button>
              <Button
                variant={notice?.variant === 'error' ? 'destructive' : 'default'}
                onClick={() => {
                  const fn = notice?.onPrimary;
                  closeNotice();
                  if (typeof fn === 'function') fn();
                }}
                disabled={isSaving}
              >
                {notice?.primaryLabel || '확인'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCreation;
